/**
 * GLAMRDiP shared horizontal scroll-progress bar primitive.
 *
 * Pattern source: the kit-hero-v2 "Included in the Kit" carousel
 * (Joel 2026-04-29). Extracted here so any horizontal carousel that
 * needs a draggable progress affordance gets it via markup, not a
 * second copy of this logic.
 *
 * Usage (markup):
 *   <div data-gd-scrollbar
 *        data-gd-scrollbar-target="<css-selector-of-scroll-track>"
 *        role="scrollbar"
 *        aria-orientation="horizontal"
 *        aria-controls="<id-of-track>"
 *        aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"
 *        tabindex="0" hidden>
 *     <div data-gd-scrollbar-fill></div>
 *   </div>
 *
 * Behaviour:
 *   - tap track  → carousel scrollTo proportional position (smooth)
 *   - drag fill  → real-time scrub
 *   - swipe      → updates fill in sync
 *   - keyboard   → ←/→ scroll by 30% of viewport, Home/End jump to ends
 *   - hidden when scrollWidth ≤ clientWidth (auto-hide on resize)
 *
 * Mobile-only is the calling section's responsibility — apply CSS
 * `display: none` on `@media (min-width: 990px)` etc. The JS doesn't
 * gate on viewport width because some sections may want the bar at
 * tablet sizes too.
 */
(() => {
  'use strict';

  const DRAG_THRESHOLD = 3;

  function init(scrollbar) {
    if (scrollbar.dataset.gdScrollbarInit === '1') return;
    scrollbar.dataset.gdScrollbarInit = '1';

    const targetSel = scrollbar.dataset.gdScrollbarTarget;
    if (!targetSel) return;
    const track = document.querySelector(targetSel);
    if (!track) return;

    let fill = scrollbar.querySelector('[data-gd-scrollbar-fill]');
    if (!fill) {
      fill = document.createElement('div');
      fill.setAttribute('data-gd-scrollbar-fill', '');
      scrollbar.appendChild(fill);
    }

    function update() {
      const overflow = track.scrollWidth - track.clientWidth;
      if (overflow <= 4) {
        scrollbar.hidden = true;
        return;
      }
      scrollbar.hidden = false;
      const visiblePct = Math.max(8, (track.clientWidth / track.scrollWidth) * 100);
      const scrollPct  = Math.max(0, Math.min(1, track.scrollLeft / overflow));
      const leftPct    = scrollPct * (100 - visiblePct);
      fill.style.width = visiblePct.toFixed(2) + '%';
      fill.style.left  = leftPct.toFixed(2) + '%';
      scrollbar.setAttribute('aria-valuenow', Math.round(scrollPct * 100).toString());
    }

    function clickJump(clientX) {
      const overflow = track.scrollWidth - track.clientWidth;
      if (overflow <= 4) return;
      const rect = scrollbar.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      track.scrollTo({ left: ratio * overflow, behavior: 'smooth' });
    }

    let drag = null;
    function startFillDrag(e) {
      const overflow = track.scrollWidth - track.clientWidth;
      if (overflow <= 4) return;
      const rect = scrollbar.getBoundingClientRect();
      const fillRect = fill.getBoundingClientRect();
      drag = {
        pointerId: e.pointerId,
        anchorX: e.clientX,
        startScrollLeft: track.scrollLeft,
        usableW: Math.max(0.0001, rect.width - fillRect.width),
        overflow,
        latestX: e.clientX,
        rafQueued: false,
        moved: false,
      };
      try { fill.setPointerCapture(e.pointerId); } catch {}
      document.body.style.userSelect = 'none';
    }
    function moveFillDrag(e) {
      if (!drag || drag.pointerId !== e.pointerId) return;
      drag.latestX = e.clientX;
      if (Math.abs(drag.latestX - drag.anchorX) > DRAG_THRESHOLD) drag.moved = true;
      if (drag.rafQueued) return;
      drag.rafQueued = true;
      window.requestAnimationFrame(() => {
        if (!drag) return;
        drag.rafQueued = false;
        const dx = drag.latestX - drag.anchorX;
        const ratio = dx / drag.usableW;
        track.scrollLeft = Math.max(0, Math.min(drag.overflow, drag.startScrollLeft + ratio * drag.overflow));
      });
    }
    function endFillDrag(e) {
      if (!drag || drag.pointerId !== e.pointerId) return;
      try { fill.releasePointerCapture(drag.pointerId); } catch {}
      document.body.style.userSelect = '';
      drag = null;
    }

    fill.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      startFillDrag(e);
    });
    fill.addEventListener('pointermove', moveFillDrag);
    fill.addEventListener('pointerup', endFillDrag);
    fill.addEventListener('pointercancel', endFillDrag);
    fill.addEventListener('lostpointercapture', endFillDrag);

    // Click-jump on the track (skip when starting on the fill — its
    // pointerdown handler stopPropagation, but defensive belt).
    let pointerDownX = null;
    scrollbar.addEventListener('pointerdown', (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      if (e.target === fill || fill.contains(e.target)) return;
      pointerDownX = e.clientX;
    });
    scrollbar.addEventListener('pointerup', (e) => {
      if (pointerDownX === null) return;
      const dx = Math.abs(e.clientX - pointerDownX);
      pointerDownX = null;
      if (dx <= DRAG_THRESHOLD) clickJump(e.clientX);
    });

    // Keyboard nav (ARIA scrollbar contract).
    scrollbar.addEventListener('keydown', (e) => {
      const overflow = track.scrollWidth - track.clientWidth;
      if (overflow <= 4) return;
      const step = track.clientWidth * 0.3;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')         { e.preventDefault(); track.scrollBy({ left: -step, behavior: 'smooth' }); }
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); track.scrollBy({ left:  step, behavior: 'smooth' }); }
      else if (e.key === 'Home') { e.preventDefault(); track.scrollTo({ left: 0, behavior: 'smooth' }); }
      else if (e.key === 'End')  { e.preventDefault(); track.scrollTo({ left: overflow, behavior: 'smooth' }); }
    });

    track.addEventListener('scroll', () => window.requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(() => window.requestAnimationFrame(update));
      ro.observe(track);
    }
    update();
  }

  function bootstrap() {
    document.querySelectorAll('[data-gd-scrollbar]').forEach(init);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
  document.addEventListener('shopify:section:load', bootstrap);
})();
